import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthStateService, AuthStatus } from './auth-state.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStatusUrl = '/auth/status';

  constructor(
    private http: HttpClient, 
    private router: Router,
    private authStateService: AuthStateService
  ) {}

  /**
   * Check the current authentication status from the server
   * @param force If true, bypass cache and check directly with the server
   * @returns Observable with the authentication status
   */
  checkAuthStatus(force: boolean = false): Observable<AuthStatus> {
    // Use cached value if available and not forced refresh
    if (!this.authStateService.needsRefresh(force)) {
      return of(this.authStateService.getStoredAuthStatus());
    }
    
    return this.http.get<AuthStatus>(this.authStatusUrl).pipe(
      tap(status => {
        this.authStateService.updateAuthStatus(status);
      }),
      catchError(error => {
        console.error('Error checking auth status:', error);
        const status: AuthStatus = { isAuthenticated: false };
        this.authStateService.updateAuthStatus(status);
        return of(status);
      })
    );
  }

  /**
   * Get the latest known authentication status without making a server request
   */
  getStoredAuthStatus(): AuthStatus {
    return this.authStateService.getStoredAuthStatus();
  }

  /**
   * Navigate to login page with optional redirect URL
   */
  redirectToLogin(redirectUrl?: string): void {
    this.authStateService.redirectToLogin(redirectUrl);
  }

  /**
   * Clear authentication state and redirect to login
   */
  logout(): void {
    this.http.get('/logout', {}).subscribe({
      next: () => {
        this.authStateService.updateAuthStatus({ isAuthenticated: false });
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('Logout error:', error);
        this.authStateService.updateAuthStatus({ isAuthenticated: false });
        this.router.navigate(['/login']);
      }
    });
  }
}